import get from 'lodash/get';
import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import EmergencyServicesContainer from './EmergencyServicesContainer';
import LeasingContainer from './LeasingContainer';

import CallCenterVideoComponent from '../Camera/CallCenterVideoComponent';
import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import CCBuildingDetailsComponent from './CCBuildingDetailsComponent';
import CallCenterStart from './CallCenterStartComponent';
import NoteComponent from './NoteComponent';

import { checkIfInIframe, isAuthenticated } from '../../utils';
import { getFullAddress } from './utils';

import CarsonLogo from '../../res/svg/CarsonLogoS.svg';
import { history } from '../../store';
import {
  CallCenterDoorButton,
  CallCenterInfoButton,
  CallCenterStyledButton,
} from '../Button';
import { CUSTOM } from '../Modal/modalTypes';
import { ButtonsSkeleton, VideoSkeleton } from '../Shared/skeletons';
import {
  StyledCallCenterButtonsContainer,
  StyledCallCenterCameraContainer,
  StyledCallCenterHomePageContainer,
  StyledCallCenterWrapper,
} from './styledComponents';

const CallCenterRouterComponent = (props) => {
  const { t } = useTransl();
  const {
    building,
    accessPoint,
    createGeneralDelivery,
    openDoor,
    noAction,
    logout,
    isLoading,
    showRemoteDoormanPopup,

    isEmergencyServicesShown,
    setIsEmergencyServicesShown,
    isLeasingShown,
    setIsLeasingShown,

    phoneNumber,
    setCurrentCameraId,
  } = props;

  const accessPointDoorId = get(accessPoint, 'doors[0].id', null);
  const defaultCameraIndex = get(building, 'cameras', []).findIndex(
    (camera) => camera.doors && camera.doors.includes(accessPointDoorId),
  );
  const doors = get(building, 'doors', []);

  const isInIframe = checkIfInIframe();
  const title = isInIframe
    ? t('callCenter.noActiveCalls')
    : t('callCenter.logInToFlex');
  const isUSABuilding = building?.country?.code === 'US';
  const generalDeliveryTitle = isUSABuilding
    ? t('delivery.usps')
    : t('delivery.mail');

  const { address1, address2, city, state, postalCode } = building;

  const fullAddress = getFullAddress([
    address1,
    address2,
    city,
    state?.code,
    postalCode,
  ]);

  if (!phoneNumber && isAuthenticated()) {
    return (
      <StyledCallCenterHomePageContainer height="100%">
        <StyledCallCenterHomePageContainer p="2rem">
          <Box sx={{ mb: '6rem', width: '50%' }}>
            <img
              style={{ width: '100%' }}
              src={CarsonLogo}
              alt={t('altLabel.carsonLogo')}
            />
          </Box>
          <Typography align="center">{title}</Typography>
          <Box mb="2rem" />
          <CallCenterStyledButton
            color="primary"
            onClick={() => {
              logout();
              history.replace('/');
            }}
          >
            {t('actions.logOut')}
          </CallCenterStyledButton>
        </StyledCallCenterHomePageContainer>
      </StyledCallCenterHomePageContainer>
    );
  }

  return (
    <section
      className="content-area"
      style={{ maxWidth: '144rem', margin: 'auto' }}
    >
      {building?.id ? (
        <StyledCallCenterWrapper>
          <StyledCallCenterButtonsContainer>
            <Typography variant="h5" align="center">
              {building.name}
            </Typography>
            <Typography variant="h6" align="center">
              {fullAddress}
            </Typography>
            <Box mt="1rem" />
            <CallCenterInfoButton
              color="primary"
              onClick={() => createGeneralDelivery(accessPoint, doors)}
            >
              {generalDeliveryTitle}
            </CallCenterInfoButton>
            <Box mt="1rem" />
            <CallCenterStyledButton
              color="secondary"
              onClick={() => setIsEmergencyServicesShown(true)}
            >
              {t('title.emergencyServices')}
            </CallCenterStyledButton>
            <Box mt="1rem" />
            {doors?.length
              ? doors
                  .filter(
                    ({ remoteDoormanAvailable }) => remoteDoormanAvailable,
                  )
                  .map((door) => {
                    const isDisabled = !door.available || !door.isActive;
                    return (
                      <Box mt="1rem" key={door.id}>
                        <CallCenterDoorButton
                          onClick={() => openDoor(door)}
                          disabled={isDisabled}
                        >
                          {`${t('actions.open')} ${door.name}`}
                        </CallCenterDoorButton>
                      </Box>
                    );
                  })
              : null}
            <Box mt="2rem" />
            <hr />
            <Box mt="2rem" />
            <CallCenterInfoButton
              color="primary"
              onClick={() => setIsLeasingShown(true)}
            >
              {t('callCenter.prospective')}
            </CallCenterInfoButton>
            <Box mt="1rem" />
            <CallCenterDoorButton onClick={() => noAction(props)}>
              {t('callCenter.noAction')}
            </CallCenterDoorButton>
            <Box mt="1rem" />
            {!!building?.remoteDoormanPopup && (
              <CallCenterDoorButton
                onClick={() =>
                  showRemoteDoormanPopup(building.remoteDoormanPopup)
                }
              >
                {t('callCenter.buildingAnnouncement')}
              </CallCenterDoorButton>
            )}
          </StyledCallCenterButtonsContainer>

          <StyledCallCenterCameraContainer>
            <CCBuildingDetailsComponent building={building} />

            {building?.cameras ? (
              <CallCenterVideoComponent
                cameras={building.cameras}
                buildingId={building.id}
                buildingName={building.name}
                defaultCameraIndex={defaultCameraIndex}
                setCurrentCameraId={setCurrentCameraId}
              />
            ) : null}
          </StyledCallCenterCameraContainer>
        </StyledCallCenterWrapper>
      ) : (
        <StyledCallCenterWrapper>
          <StyledCallCenterButtonsContainer>
            <ButtonsSkeleton />
          </StyledCallCenterButtonsContainer>
          <StyledCallCenterCameraContainer>
            <VideoSkeleton />
          </StyledCallCenterCameraContainer>
        </StyledCallCenterWrapper>
      )}

      <NoteComponent note={building?.callCenterNote} />

      <Box mt="3rem" mb="1rem">
        <Typography variant="h5" align="center">
          {t('callCenter.callType')}
        </Typography>
      </Box>

      <CallCenterStart {...props} />

      {phoneNumber && isAuthenticated() && !isLoading && !building?.id ? (
        <CallCenterStyledButton
          color="primary"
          onClick={() => window.location.reload()}
        >
          {t('callCenter.reload')}
        </CallCenterStyledButton>
      ) : null}
      {isEmergencyServicesShown && (
        <ModalHandlerComponent
          modal={{
            modalType: CUSTOM,
            modalProps: { title: t('title.emergencyServices') },
          }}
          onModalClose={() => setIsEmergencyServicesShown(false)}
        >
          <EmergencyServicesContainer
            accessPoint={accessPoint}
            setIsEmergencyServicesShown={setIsEmergencyServicesShown}
            building={building}
          />
        </ModalHandlerComponent>
      )}
      {isLeasingShown && building?.id && (
        <ModalHandlerComponent
          modal={{
            modalType: CUSTOM,
            modalProps: { title: t('callCenter.prospective') },
          }}
          onModalClose={() => setIsLeasingShown(false)}
        >
          <LeasingContainer
            building={building}
            setIsLeasingShown={setIsLeasingShown}
          />
        </ModalHandlerComponent>
      )}
    </section>
  );
};

CallCenterRouterComponent.defaultProps = {
  accessPoint: {},
  building: {},
  phoneNumber: null,
};

CallCenterRouterComponent.propTypes = {
  location: PropTypes.instanceOf(Object).isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  building: PropTypes.instanceOf(Object),
  accessPoint: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool.isRequired,
  isEmergencyServicesShown: PropTypes.bool.isRequired,
  setIsEmergencyServicesShown: PropTypes.func.isRequired,
  isLeasingShown: PropTypes.bool.isRequired,
  setIsLeasingShown: PropTypes.func.isRequired,
  createGeneralDelivery: PropTypes.func.isRequired,
  openAccessPointForEmergency: PropTypes.func.isRequired,
  openDoor: PropTypes.func.isRequired,
  noAction: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  setCurrentCameraId: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string,
  showRemoteDoormanPopup: PropTypes.func.isRequired,
};

export default CallCenterRouterComponent;
